import {Filter} from '@github-ui/filter'
import {useQuery} from '@github-ui/react-query'
import {Link, useParams} from '@github-ui/react-router'
import {ShortcutEditingHeader, type UserShortcut} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'

import {SavedViewMutationErrorBanner} from '../../components/views/SavedViewMutationErrorBanner'
import {LABELS} from '../../constants/labels'
import {useConfirmDiscardChanges} from '../../hooks/use-confirm-discard-changes'
import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import {
  SAVED_VIEW_OPEN_SOURCES,
  type SavedViewOpenLocationState,
  useSavedViewAnalytics,
} from '../../hooks/use-saved-view-analytics'
import {useSavedViewEditor} from '../../hooks/use-saved-view-editor'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {savedViewsQueryOptions} from '../../queries/saved-views'
import styles from './RepositoryViewNewPage.module.css'
import {RepositoryViewNewPreview} from './RepositoryViewNewPreview'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

export function RepositoryViewNewContent() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()

  const viewsPath = `/${owner}/${repo}/issues/views`

  const {data: savedViewsData} = useQuery(savedViewsQueryOptions({owner, repo}))
  const viewerCanManageViews = savedViewsData?.viewerCanManageViews
  useEffect(() => {
    if (viewerCanManageViews === false) {
      navigate(viewsPath, {replace: true})
    }
  }, [navigate, viewerCanManageViews, viewsPath])

  const {trackNewVisited} = useSavedViewAnalytics()
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackNewVisited()
  }, [trackNewVisited])

  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue, isDirty} =
    useSavedViewEditor({
      initialShortcut: INITIAL_SHORTCUT,
    })

  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty})

  const filterProviders = useSavedViewFilterProviders({owner, repo})

  const {mutate, isPending, error} = useCreateSavedViewMutation({owner, repo})

  const handleDismiss = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      navigate(viewsPath)
    }
  }, [confirmDiscardIfDirty, navigate, viewsPath])

  const handleBackLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (!isDirty) return

      event.preventDefault()
      void handleDismiss()
    },
    [handleDismiss, isDirty],
  )

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
        source: 'new',
      },
      {
        onSuccess: ({savedView}) => {
          const openState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.created}
          navigate(`${viewsPath}/${savedView.id}`, {state: openState})
        },
      },
    )
  }, [mutate, navigate, viewsPath, shortcut])

  if (viewerCanManageViews === false) {
    return null
  }

  return (
    <div className={styles.middlePaneWrapper} data-hpc>
      <Link to={viewsPath} className={styles.backLink} onClick={handleBackLinkClick}>
        <ArrowLeftIcon />
        Back to views
      </Link>
      <Heading as="h1" variant="medium" className={styles.heading}>
        Create view
      </Heading>
      <div className={styles.middlePaneGrid}>
        <SavedViewMutationErrorBanner
          message={error ? LABELS.views.createError : null}
          onRetry={handleSave}
          isRetrying={isPending}
        />
        <ShortcutEditingHeader
          shortcut={shortcut}
          setShortcutValue={setShortcutValue}
          onSave={handleSave}
          onDismiss={handleDismiss}
          isSaving={isPending}
          helperText="Anyone with repository access can see this view."
          renderFilter={({inputRef, className}) => (
            <Filter
              id="repo-view-new-filter"
              className={className}
              inputRef={inputRef}
              label="Query"
              placeholder="Search issues"
              variant="input"
              providers={filterProviders}
              filterValue={shortcut.query}
              onChange={value => setShortcutValue('query', value)}
              onSubmit={request => commitPreviewQuery(request.raw)}
              settings={{aliasMatching: true, groupAndKeywordSupport: true}}
            />
          )}
        />
        <RepositoryViewNewPreview
          owner={owner}
          repo={repo}
          query={previewQuery}
          previewQueryContextValue={previewQueryContextValue}
        />
      </div>
    </div>
  )
}
